import { Component, OnInit, effect, inject, signal, untracked, ViewEncapsulation } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastService } from 'angular-toastify';
import { CookieService } from 'ngx-cookie-service';

// Angular Material Datepicker
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule, MAT_DATE_FORMATS, MAT_DATE_LOCALE, DateAdapter, NativeDateAdapter } from '@angular/material/core';

// Shared Components
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';
import { PssConfirmModalComponent } from '../../../../tools/template/pss/pss-confirm-modal/pss-confirm-modal.component';
import { ModalParkingFineDetailComponent } from '../../modals/finance/parking-finance/modal-parking-fine-detail/modal-parking-fine-detail.component';
import { ModalParkingFineReceiptComponent } from '../../modals/finance/parking-finance/modal-parking-fine-receipt/modal-parking-fine-receipt.component';

// Services, models, and mappers
import { ApiParkingFinanceService } from '../../../../core/services/api/finance/api-parking-finance.service';
import { MapperParkingFinanceService } from '../../../../core/mappers/finance/mapper-parking-finance.service';
import { SiteStateService } from '../../../../core/services/site-state.service';
import { MenuPermissionService } from '../../../../core/services/menu-permission.service';
import {
  ParkingFinanceListRow,
  ParkingFinanceDashboardStat,
  ParkingFinanceRecentActivityDto,
  ParkingFinanceChartItemDto,
  ParkingFinanceFilterOptionsDto,
  CancelParkingFinancePayload,
  IssueFullTaxInvoiceFromFinanceResult
} from '../../../../core/models/finance/parking-finance.model';

// Custom Date Format — Gregorian (ค.ศ.) DD/MM/YYYY
export const CUSTOM_DATE_FORMATS = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: { day: '2-digit', month: '2-digit', year: 'numeric' } as Intl.DateTimeFormatOptions,
    monthYearLabel: { year: 'numeric', month: 'short' } as Intl.DateTimeFormatOptions,
    dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' } as Intl.DateTimeFormatOptions,
    monthYearA11yLabel: { year: 'numeric', month: 'long' } as Intl.DateTimeFormatOptions,
  }
};

@Component({
  selector: 'app-parking-finance',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [
    CommonModule,
    FormsModule,
    MatDatepickerModule,
    MatNativeDateModule,
    PssLoadingComponent,
    PssConfirmModalComponent,
    ModalParkingFineDetailComponent,
    ModalParkingFineReceiptComponent
  ],
  providers: [
    DatePipe,
    { provide: DateAdapter, useClass: NativeDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS },
    { provide: MAT_DATE_LOCALE, useValue: 'en-GB' }
  ],
  templateUrl: './parking-finance.component.html',
  styleUrl: './parking-finance.component.css'
})
export class ParkingFinanceComponent implements OnInit {
  // Dependency Injections
  readonly siteState = inject(SiteStateService);
  private readonly router = inject(Router);
  private readonly permissionService = inject(MenuPermissionService);
  private readonly apiService = inject(ApiParkingFinanceService);
  private readonly mapper = inject(MapperParkingFinanceService);
  private readonly toast = inject(ToastService);
  private readonly cookieService = inject(CookieService);
  private readonly datePipe = inject(DatePipe);

  // States & Core Lists
  records: ParkingFinanceListRow[] = [];
  isLoading = signal(false);
  isDashboardLoading = signal(false);
  isCancelling = signal(false);
  lastFetchedTime: Date | null = null;
  userId = 0;
  menuId = 0;

  // Search & Pagination States
  searchText = '';
  searchType: 'all' | 'receiptid' | 'feename' | 'feetypename' | 'sitename' | 'status' = 'all';
  isSearchTypeDropdownOpen = false;
  showTableFilters = false;

  currentPage = 1;
  pageSize = 20;
  pageSizeOptions: number[] = [10, 20, 50, 100];
  totalItems = 0;
  totalPages = 1;
  isPageSizeDropdownOpen = false;

  sortColumn = 'id';
  sortDirection: 'asc' | 'desc' = 'desc';

  // Date range filter (always required — defaults to today)
  startDate!: Date;
  endDate!: Date;
  minTotal: number | null = null;

  // Autocomplete Select states (replaces standard select)
  searchFeeTypeText = '';
  filterFeeType = '';
  isFeeTypeDropdownOpen = false;

  searchFeeNameText = '';
  filterFeeName = '';
  isFeeNameDropdownOpen = false;

  searchStatusText = '';
  filterStatus = '';
  isStatusDropdownOpen = false;

  // Dashboard Data
  showDashboard = true;
  dashboardStats: ParkingFinanceDashboardStat[] = [];
  recentActivity: ParkingFinanceRecentActivityDto[] = [];
  chartItems: ParkingFinanceChartItemDto[] = [];
  filterOptions: ParkingFinanceFilterOptionsDto = { feeTypes: [], feeNames: [], statuses: [] };
  dashboardSummary: any = null;

  get normalPercent(): number {
    if (!this.dashboardSummary || !this.dashboardSummary.totalCount) return 100;
    return Math.round((this.dashboardSummary.normalCount / this.dashboardSummary.totalCount) * 100);
  }

  get cancelledPercent(): number {
    if (!this.dashboardSummary || !this.dashboardSummary.totalCount) return 0;
    return Math.round((this.dashboardSummary.cancelledCount / this.dashboardSummary.totalCount) * 100);
  }

  // Dashboard Chart States
  chartMode: 'daily' | 'monthly' = 'daily';
  dailySubMode: 'default' | 'custom' = 'default';
  chartType: 'line' | 'bar' = 'line';
  customStartDate = '';
  customEndDate = '';
  selectedMonthStart = new Date().toISOString().slice(0, 7);
  selectedMonthEnd = new Date().toISOString().slice(0, 7);

  yAxisLabels: number[] = [];
  currentMonthPoints = '';
  currentMonthAreaPoints = '';
  currentMonthDots: Array<{ x: number; y: number; label: string; subLabel: string; value: number; color: string }> = [];
  chartTotalCount = 0;
  chartWidth = '100%';

  // Cancellation Confirmation Modal State
  isCancelModalOpen = false;
  selectedRecordForCancel: ParkingFinanceListRow | null = null;
  cancelRemark = '';

  // Detail Modal State
  isDetailModalOpen = false;
  selectedRecordDetail: ParkingFinanceListRow | null = null;

  // Receipt Modal State
  isReceiptModalOpen = false;
  selectedRecordReceipt: ParkingFinanceListRow | null = null;

  constructor() {
    const today = this.getTodayDate();
    this.startDate = new Date(today);
    this.endDate = new Date(today);

    // Read user information
    const storedUserId = localStorage.getItem('userId');
    this.userId = storedUserId ? Number(storedUserId) : Number(this.cookieService.get('user_id'));

    // Automatically reload data when the site selection updates in the sidebar
    effect(() => {
      const site = this.siteState.selectedSite();
      if (!site?.id || !this.userId) return;

      untracked(() => {
        this.currentPage = 1;
        this.loadTable();
        this.loadMarketingDashboard();
      });
    });
  }

  ngOnInit(): void {
    // Lookup current MenuID for edit permission checking
    this.resolveMenuId();
  }

  // ================= Normal Functions =================

  /** Find the menu listing ID for the current route path to authenticate write permissions */
  private resolveMenuId(): void {
    const currentUrl = this.router.url.split('?')[0];
    this.permissionService.loadUserMenus().subscribe({
      next: () => {
        const groups = this.permissionService.menuGroups();
        for (const group of groups) {
          if (!group.children) continue;
          for (const child of group.children) {
            if (child.url === currentUrl) {
              this.menuId = child.id;
              return;
            }
            if (child.submenu) {
              for (const sub of child.submenu) {
                if (sub.url === currentUrl) {
                  this.menuId = sub.id;
                  return;
                }
              }
            }
          }
        }
      },
      error: (err: any) => console.error('[ParkingFinance] Failed to load menus for checking menuId', err),
    });
  }

  /** Safe navigation getter for active site selection ID */
  get selectedSiteId(): number {
    return this.siteState.selectedSite()?.id ?? 0;
  }

  /** Check if the user has permission to view the page (Read permission) */
  get hasReadPermission(): boolean {
    const currentUrl = this.router.url.split('?')[0];
    return this.permissionService.canAccessRoute(currentUrl);
  }

  /** Check if the user has permission to create records */
  get hasCreatePermission(): boolean {
    const currentUrl = this.router.url.split('?')[0];
    return this.permissionService.canCreate(currentUrl);
  }

  /** Check if the user has permission to update/edit/cancel records */
  get hasUpdatePermission(): boolean {
    const currentUrl = this.router.url.split('?')[0];
    return this.permissionService.canUpdate(currentUrl);
  }

  /** Check if the user has permission to delete records */
  get hasDeletePermission(): boolean {
    const currentUrl = this.router.url.split('?')[0];
    return this.permissionService.canDelete(currentUrl);
  }

  /** Check if the user has permission to cancel (compatibility getter for hasUpdatePermission) */
  get hasEditPermission(): boolean {
    return this.hasUpdatePermission;
  }

  /** Toggle marketing summary dashboard section */
  toggleDashboard(): void {
    this.showDashboard = !this.showDashboard;
  }

  /** Toggles advanced search filter panel */
  toggleTableFilters(): void {
    this.showTableFilters = !this.showTableFilters;
  }

  /** Refresh table and dashboard values */
  onRefresh(): void {
    this.searchText = '';
    this.searchType = 'all';
    const today = this.getTodayDate();
    this.startDate = new Date(today);
    this.endDate = new Date(today);
    this.minTotal = null;
    this.filterFeeType = '';
    this.searchFeeTypeText = '';
    this.filterFeeName = '';
    this.searchFeeNameText = '';
    this.filterStatus = '';
    this.searchStatusText = '';
    this.currentPage = 1;

    this.loadTable();
    this.loadMarketingDashboard();
  }

  /** Run search when user types or updates criteria */
  onSearchChange(): void {
    this.currentPage = 1;
    this.loadTable();
  }

  /** Toggle Search parameters types menu option dropdown */
  toggleSearchTypeDropdown(): void {
    this.isSearchTypeDropdownOpen = !this.isSearchTypeDropdownOpen;
  }

  /** Assign search parameters type */
  selectSearchType(type: 'all' | 'receiptid' | 'feename' | 'feetypename' | 'sitename' | 'status'): void {
    this.searchType = type;
    this.isSearchTypeDropdownOpen = false;
    this.onSearchChange();
  }

  get currentSearchTypeLabel(): string {
    switch (this.searchType) {
      case 'all': return 'ทั้งหมด';
      case 'receiptid': return 'เลขใบเสร็จ';
      case 'feename': return 'ชื่อ Fee';
      case 'feetypename': return 'ประเภทชำระ';
      case 'sitename': return 'ชื่อ Site';
      case 'status': return 'สถานะ';
      default: return 'ค้นหา';
    }
  }

  /** Custom Datepicker Change event wrapper */
  onDateChange(): void {
    this.normalizeDateRange();
    this.currentPage = 1;
    this.loadTable();
    this.loadMarketingDashboard();
  }

  /** Autocomplete filter options getter for FeeType */
  get filteredFeeTypeOptions(): string[] {
    const q = this.searchFeeTypeText.trim().toLowerCase();
    const opts = this.filterOptions?.feeTypes || [];
    if (!q) return opts;
    return opts.filter((opt: string) => opt.toLowerCase().includes(q));
  }

  /** Select FeeType option */
  selectFeeType(option: string): void {
    this.filterFeeType = option;
    this.searchFeeTypeText = option;
    this.isFeeTypeDropdownOpen = false;
    this.currentPage = 1;
    this.loadTable();
  }

  /** Closes fee type dropdown and validates input text matching options */
  closeFeeTypeDropdown(): void {
    const val = this.searchFeeTypeText.trim();
    const opts = this.filterOptions?.feeTypes || [];
    const match = opts.find((o: string) => o.toLowerCase() === val.toLowerCase());

    if (match) {
      this.filterFeeType = match;
      this.searchFeeTypeText = match;
    } else if (val === '') {
      this.filterFeeType = '';
      this.searchFeeTypeText = '';
    } else {
      this.searchFeeTypeText = this.filterFeeType || '';
    }
    this.isFeeTypeDropdownOpen = false;
    this.currentPage = 1;
    this.loadTable();
  }

  /** Autocomplete filter options getter for FeeName */
  get filteredFeeNameOptions(): string[] {
    const q = this.searchFeeNameText.trim().toLowerCase();
    const opts = this.filterOptions?.feeNames || [];
    if (!q) return opts;
    return opts.filter((opt: string) => opt.toLowerCase().includes(q));
  }

  /** Select FeeName option */
  selectFeeName(option: string): void {
    this.filterFeeName = option;
    this.searchFeeNameText = option;
    this.isFeeNameDropdownOpen = false;
    this.currentPage = 1;
    this.loadTable();
  }

  /** Closes fee name dropdown and validates input text matching options */
  closeFeeNameDropdown(): void {
    const val = this.searchFeeNameText.trim();
    const opts = this.filterOptions?.feeNames || [];
    const match = opts.find((o: string) => o.toLowerCase() === val.toLowerCase());

    if (match) {
      this.filterFeeName = match;
      this.searchFeeNameText = match;
    } else if (val === '') {
      this.filterFeeName = '';
      this.searchFeeNameText = '';
    } else {
      this.searchFeeNameText = this.filterFeeName || '';
    }
    this.isFeeNameDropdownOpen = false;
    this.currentPage = 1;
    this.loadTable();
  }

  /** Autocomplete filter options getter for Status */
  get filteredStatusOptions(): string[] {
    const q = this.searchStatusText.trim().toLowerCase();
    const opts = this.filterOptions?.statuses || [];
    if (!q) return opts;
    return opts.filter((opt: string) => opt.toLowerCase().includes(q));
  }

  /** Select Status option */
  selectStatus(option: string): void {
    this.filterStatus = option;
    this.searchStatusText = option;
    this.isStatusDropdownOpen = false;
    this.currentPage = 1;
    this.loadTable();
  }

  /** Closes status dropdown and validates input text matching options */
  closeStatusDropdown(): void {
    const val = this.searchStatusText.trim();
    const opts = this.filterOptions?.statuses || [];
    const match = opts.find((o: string) => o.toLowerCase() === val.toLowerCase());

    if (match) {
      this.filterStatus = match;
      this.searchStatusText = match;
    } else if (val === '') {
      this.filterStatus = '';
      this.searchStatusText = '';
    } else {
      this.searchStatusText = this.filterStatus || '';
    }
    this.isStatusDropdownOpen = false;
    this.currentPage = 1;
    this.loadTable();
  }

  /** Sorting trigger for table columns click headers */
  sortData(column: string): void {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'desc';
    }
    this.currentPage = 1;
    this.loadTable();
  }

  /** Standard pagination range helper methods */
  get startItemIndex(): number {
    if (this.totalItems === 0) return 0;
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get endItemIndex(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalItems);
  }

  get pagesArray(): number[] {
    const maxVisibleButtons = 5;
    let startPage = Math.max(1, this.currentPage - 2);
    let endPage = Math.min(this.totalPages, startPage + maxVisibleButtons - 1);

    if (endPage - startPage < maxVisibleButtons - 1) {
      startPage = Math.max(1, endPage - maxVisibleButtons + 1);
    }
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  }

  setPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadTable();
    }
  }

  togglePageSizeDropdown(): void {
    this.isPageSizeDropdownOpen = !this.isPageSizeDropdownOpen;
  }

  closePageSizeDropdown(): void {
    this.isPageSizeDropdownOpen = false;
  }

  selectPageSize(size: number): void {
    this.pageSize = size;
    this.currentPage = 1;
    this.isPageSizeDropdownOpen = false;
    this.loadTable();
  }

  /** Chart mode select handler */
  setChartMode(mode: 'daily' | 'monthly'): void {
    this.chartMode = mode;
    if (mode === 'monthly') {
      const today = new Date();
      const currentMonthStr = today.toISOString().slice(0, 7);
      const prevMonthDate = new Date(today.getFullYear(), today.getMonth() - 2, 1);
      this.selectedMonthStart = prevMonthDate.toISOString().slice(0, 7);
      this.selectedMonthEnd = currentMonthStr;
    }
    this.processDashboardChart();
  }

  /** Chart daily detail display mode select handler */
  setDailySubMode(mode: 'default' | 'custom'): void {
    this.dailySubMode = mode;
    if (mode === 'custom') {
      if (!this.customStartDate) {
        const s = new Date();
        s.setDate(s.getDate() - 6);
        this.customStartDate = this.formatDateString(s);
      }
      if (!this.customEndDate) {
        const e = new Date();
        this.customEndDate = this.formatDateString(e);
      }
      this.onCustomChartDateChange();
    } else {
      this.processDashboardChart();
    }
  }

  setChartType(type: 'line' | 'bar'): void {
    this.chartType = type;
  }

  /** Safe navigation getter for custom date input validation limits */
  get maxCustomDate(): string {
    if (!this.customStartDate) return '';
    const d = new Date(this.customStartDate);
    d.setDate(d.getDate() + 9); // Max 10 days constraint range
    return this.formatDateString(d);
  }

  get maxMonthEnd(): string {
    if (!this.selectedMonthStart) return '';
    const start = new Date(this.selectedMonthStart + '-01');
    const maxDate = new Date(start);
    maxDate.setMonth(maxDate.getMonth() + 11);
    return maxDate.toISOString().slice(0, 7);
  }

  onCustomChartDateChange(): void {
    if (!this.customStartDate || !this.customEndDate) return;
    const s = new Date(this.customStartDate);
    const e = new Date(this.customEndDate);
    if (e < s) this.customEndDate = this.customStartDate;

    const diffDays = Math.ceil((e.getTime() - s.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays > 10) {
      const fixedEnd = new Date(s);
      fixedEnd.setDate(s.getDate() + 9);
      this.customEndDate = this.formatDateString(fixedEnd);
    }
    this.processDashboardChart();
  }

  onChartMonthChange(): void {
    if (this.selectedMonthStart && this.selectedMonthEnd) {
      const start = new Date(this.selectedMonthStart + '-01');
      const end = new Date(this.selectedMonthEnd + '-01');

      if (end < start) {
        this.selectedMonthEnd = this.selectedMonthStart;
      } else {
        const diffMonths = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
        if (diffMonths > 11) {
          const fixedEnd = new Date(start);
          fixedEnd.setMonth(start.getMonth() + 11);
          this.selectedMonthEnd = fixedEnd.toISOString().slice(0, 7);
        }
      }
    }
    this.processDashboardChart();
  }

  resetCustomDate(): void {
    const e = new Date();
    const s = new Date();
    s.setDate(e.getDate() - 9);
    this.customStartDate = this.formatDateString(s);
    this.customEndDate = this.formatDateString(e);
    this.processDashboardChart();
  }

  processDashboardChart(): void {
    return; // Chart section disabled in template
    const data = this.chartItems || [];
    let xLabels: Date[] = [];
    let dataPoints: number[] = [];

    if (this.chartMode === 'daily') {
      let start: Date;
      let end: Date;
      if (this.dailySubMode === 'custom' && this.customStartDate && this.customEndDate) {
        start = new Date(this.customStartDate);
        end = new Date(this.customEndDate);
      } else {
        start = new Date();
        start.setDate(start.getDate() - 6);
        end = new Date();
      }

      // Generate sequence of dates
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        xLabels.push(new Date(d));
        dataPoints.push(0);
      }

      data.forEach((item) => {
        if (!item.paidDate) return;
        const d = new Date(item.paidDate);
        const dateOnly = new Date(d.getFullYear(), d.getMonth(), d.getDate());

        const idx = xLabels.findIndex(x => x.toDateString() === dateOnly.toDateString());
        if (idx !== -1) {
          dataPoints[idx] += item.fee;
        }
      });
    } else {
      // Monthly chart visualization
      const [startYear, startMonth] = this.selectedMonthStart.split('-').map(Number);
      const [endYear, endMonth] = this.selectedMonthEnd.split('-').map(Number);
      const start = new Date(startYear, startMonth - 1, 1);
      const end = new Date(endYear, endMonth, 0);

      const diffTime = Math.abs(end.getTime() - start.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays <= 60) {
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
          xLabels.push(new Date(d));
          dataPoints.push(0);
        }
        data.forEach((item) => {
          if (!item.paidDate) return;
          const d = new Date(item.paidDate);
          if (d >= start && d <= end) {
            const idx = xLabels.findIndex(x => x.toDateString() === d.toDateString());
            if (idx !== -1) dataPoints[idx] += item.fee;
          }
        });
      } else {
        let temp = new Date(startYear, startMonth - 1, 1);
        while (temp <= end) {
          xLabels.push(new Date(temp));
          dataPoints.push(0);
          temp.setMonth(temp.getMonth() + 1);
        }

        data.forEach((item) => {
          if (!item.paidDate) return;
          const d = new Date(item.paidDate);
          if (d >= start && d <= end) {
            const idx = xLabels.findIndex(x => x.getFullYear() === d.getFullYear() && x.getMonth() === d.getMonth());
            if (idx !== -1) dataPoints[idx] += item.fee;
          }
        });
      }
    }

    this.chartTotalCount = dataPoints.reduce((a, b) => a + b, 0);

    const rawMax = Math.max(...dataPoints, 1000);
    const maxVal = Math.ceil(rawMax / 4) * 4;
    this.yAxisLabels = [0, maxVal * 0.25, maxVal * 0.5, maxVal * 0.75, maxVal];

    const getCoordinate = (val: number, index: number, total: number) => {
      if (total <= 1) return { x: 50, y: 100 - ((val / maxVal) * 100) };
      const x = 2 + (index / (total - 1)) * 96;
      const y = 100 - ((val / maxVal) * 100);
      return { x, y };
    };

    const pointsObj = dataPoints.map((val, i) => getCoordinate(val, i, dataPoints.length));
    this.currentMonthPoints = pointsObj.map(p => `${p.x},${p.y}`).join(' ');

    const getThaiMonth = (monthIndex: number) => {
      const months = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];
      return months[monthIndex];
    };

    this.currentMonthDots = pointsObj.map((p, i) => {
      const date = xLabels[i];
      let label = `${date.getDate()}`;
      let subLabel = '';

      if (date.getDate() === 1 || i === 0) {
        subLabel = getThaiMonth(date.getMonth());
      }

      if (this.chartMode === 'monthly' && dataPoints.length <= 12 && xLabels.length <= 12) {
        label = getThaiMonth(date.getMonth());
        subLabel = `'${(date.getFullYear() + 543).toString().slice(2)}`;
      }

      return {
        x: p.x,
        y: p.y,
        label,
        subLabel,
        value: dataPoints[i],
        color: this.getChartColor(i)
      };
    });

    if (pointsObj.length > 0) {
      this.currentMonthAreaPoints = `
        ${pointsObj[0].x},100
        ${this.currentMonthPoints}
        ${pointsObj[pointsObj.length - 1].x},100
      `;
    } else {
      this.currentMonthAreaPoints = '';
    }

    if (dataPoints.length > 30) {
      this.chartWidth = `${dataPoints.length * 40}px`;
    } else {
      this.chartWidth = '100%';
    }
  }

  getChartColor(index: number): string {
    const colors = ['#fbbf24', '#f59e0b', '#34d399', '#60a5fa', '#f87171', '#818cf8', '#a78bfa', '#f472b6'];
    return colors[index % colors.length];
  }

  getRandomColor(id: number): string {
    const colors = ['bg-blue-500', 'bg-green-500', 'bg-yellow-500', 'bg-purple-500', 'bg-pink-500', 'bg-indigo-500'];
    return colors[id % colors.length];
  }

  /** Time calculation formula for activity timeline feed entries */
  getTimeAgo(dateInput: string | null): string {
    if (!dateInput) return '-';
    const date = new Date(dateInput);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    let interval = seconds / 31536000;
    if (interval > 1) return Math.floor(interval) + ' ปีที่แล้ว';
    interval = seconds / 2592000;
    if (interval > 1) return Math.floor(interval) + ' เดือนที่แล้ว';
    interval = seconds / 86400;
    if (interval > 1) return Math.floor(interval) + ' วันที่แล้ว';
    interval = seconds / 3600;
    if (interval > 1) return Math.floor(interval) + ' ชม.ที่แล้ว';
    interval = seconds / 60;
    if (interval > 1) return Math.floor(interval) + ' นาทีที่แล้ว';
    return 'เมื่อครู่';
  }

  /** Display date conversion wrapper */
  private getTodayDate(): Date {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }

  /** Ensure start/end dates are never empty and start <= end */
  private normalizeDateRange(): void {
    const today = this.getTodayDate();
    if (!this.startDate) this.startDate = new Date(today);
    if (!this.endDate) this.endDate = new Date(today);
    if (this.startDate > this.endDate) {
      this.endDate = new Date(this.startDate);
    }
  }

  formatDateDisplay(date: Date | string | null | undefined): string {
    if (!date) return '-';
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return '-';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hh}:${mm}`;
  }

  /** Internal Date objects serializer helper */
  private formatDateString(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  // ================= API Functions =================

  /** Fetch and map table row lists */
  loadTable(): void {
    const siteId = this.selectedSiteId;
    if (!siteId || !this.userId) return;

    this.isLoading.set(true);
    this.normalizeDateRange();

    const query = {
      siteId,
      userId: this.userId,
      page: this.currentPage,
      pageSize: this.pageSize,
      startDate: this.formatDateString(this.startDate),
      endDate: this.formatDateString(this.endDate)
    } as any;

    if (this.searchText.trim()) {
      query.searchField = this.mapper.mapSearchField(this.searchType);
      query.searchText = this.searchText.trim();
    }
    if (this.filterFeeType) query.feeType = this.filterFeeType;
    if (this.filterFeeName) query.feeName = this.filterFeeName;
    if (this.filterStatus) query.status = this.filterStatus;
    if (this.minTotal && this.minTotal > 0) query.minTotal = this.minTotal;

    this.apiService.getParkingFinanceForTable(query).subscribe({
      next: (res: any) => {
        console.log('getParkingFinanceForTable response:', res);
        const mapped = this.mapper.mapTableFromDto(res);
        this.records = mapped.items;
        this.totalItems = mapped.total;
        this.totalPages = Math.ceil(this.totalItems / this.pageSize) || 1;
        this.lastFetchedTime = new Date();
        this.isLoading.set(false);
      },
      error: (err: any) => {
        console.error('Failed to load table records:', err);
        this.toast.error('ไม่สามารถดึงข้อมูลตารางรับชำระได้');
        this.isLoading.set(false);
      }
    });
  }

  /** Fetch and map dashboard values and charts */
  loadMarketingDashboard(): void {
    const siteId = this.selectedSiteId;
    if (!siteId || !this.userId) return;

    this.isDashboardLoading.set(true);
    this.normalizeDateRange();

    const startStr = this.formatDateString(this.startDate);
    const endStr = this.formatDateString(this.endDate);

    this.apiService.getParkingFinanceMarketingDashboard(siteId, this.userId, startStr, endStr).subscribe({
      next: (res: any) => {
        console.log('getParkingFinanceMarketingDashboard response:', res);
        this.dashboardSummary = res.summary;
        const mapped = this.mapper.mapMarketingDashboard(res);
        this.dashboardStats = mapped.stats;
        this.recentActivity = mapped.recentActivity;
        // Chart disabled — skip chartItems / processDashboardChart
        // this.chartItems = mapped.chartItems;
        // this.processDashboardChart();
        this.filterOptions = mapped.filterOptions;
        this.isDashboardLoading.set(false);
      },
      error: (err: any) => {
        console.error('Failed to load marketing dashboard stats:', err);
        this.isDashboardLoading.set(false);
      }
    });
  }

  /** Cancel button click handler - opens the confirm modal */
  onCancelClick(record: ParkingFinanceListRow): void {
    if (!this.hasEditPermission) {
      this.toast.error('คุณไม่มีสิทธิ์ยกเลิกรายการ');
      return;
    }
    this.selectedRecordForCancel = record;
    this.cancelRemark = '';
    this.isCancelModalOpen = true;
  }

  closeCancelModal(): void {
    this.isCancelModalOpen = false;
    this.selectedRecordForCancel = null;
    this.cancelRemark = '';
  }

  /** Checks if the paidDate matches today's date */
  isToday(dateStr: string | null | undefined): boolean {
    if (!dateStr) return false;
    const d = new Date(dateStr);
    const today = new Date();
    return d.getFullYear() === today.getFullYear() &&
      d.getMonth() === today.getMonth() &&
      d.getDate() === today.getDate();
  }

  /** Checks if the finance record is eligible for full tax invoice request */
  canIssueFullTaxInvoice(record: ParkingFinanceListRow | null | undefined): boolean {
    if (!record || !this.hasEditPermission) return false;

    const status = String(record.statusId ?? '').trim();
    if (status !== '0' && status !== '1') return false;
    if (!this.isToday(record.paidDate)) return false;

    const receiptId = (record.receiptId || '').trim();
    if (receiptId.toUpperCase().startsWith('RL')) return false;

    return true;
  }

  /** Opens the details view modal for a given record row */
  onViewDetail(record: ParkingFinanceListRow): void {
    this.selectedRecordDetail = record;
    this.isDetailModalOpen = true;
  }

  /** Closes the details view modal */
  closeDetailModal(): void {
    this.isDetailModalOpen = false;
    this.selectedRecordDetail = null;
  }

  /** Handles cancellation event from the child detail modal component */
  onCancelFromDetailComponent(record: ParkingFinanceListRow): void {
    this.closeDetailModal();
    this.onCancelClick(record);
  }

  /** Handles receipt modal trigger from child detail component */
  onViewReceiptFromDetailComponent(record: ParkingFinanceListRow): void {
    this.closeDetailModal();
    this.selectedRecordReceipt = record;
    this.isReceiptModalOpen = true;
  }

  /** Closes receipt modal view */
  closeReceiptModal(): void {
    this.isReceiptModalOpen = false;
    this.selectedRecordReceipt = null;
  }

  /** Handles successful full tax invoice issue from receipt modal */
  onIssueFullTaxFromReceipt(result: IssueFullTaxInvoiceFromFinanceResult): void {
    this.closeReceiptModal();
    this.loadTable();
    this.loadMarketingDashboard();

    if (result.newReceiptId) {
      this.toast.success(`ออกใบกำกับภาษีเรียบร้อย — เลขใบเสร็จใหม่: ${result.newReceiptId}`);
    }
  }

  /** Confirms and submits the cancel operation to the backend API */
  onConfirmCancel(): void {
    const record = this.selectedRecordForCancel;
    if (!record || !this.userId || !this.selectedSiteId || !this.menuId) return;

    this.isCancelModalOpen = false;
    this.isCancelling.set(true);

    const payload: CancelParkingFinancePayload = {
      menuId: this.menuId,
      userId: this.userId,
      siteId: this.selectedSiteId,
      financeId: record.id,
      remark: this.cancelRemark.trim() || undefined
    };

    this.apiService.requestCancelParkingFinance(payload).subscribe({
      next: (res: any) => {
        console.log('requestCancelParkingFinance response:', res);
        const result = this.mapper.mapCancelResponse(res);
        if (result.isSuccess) {
          this.toast.success(result.message || 'ดำเนินการเรียบร้อย');
          // Reload datasets
          this.loadTable();
          this.loadMarketingDashboard();
        } else {
          this.toast.error(result.message || 'ไม่สามารถขอยกเลิกได้');
        }
        this.isCancelling.set(false);
      },
      error: (err: any) => {
        console.error('Failed to cancel parking finance entry:', err);
        this.toast.error('เกิดข้อผิดพลาดในการยกเลิกรายการ');
        this.isCancelling.set(false);
      }
    });
  }
}
